import { getCollectionOrders } from "@/api/collectionOrder";
import { useState, useEffect } from "react";
import { DateRange } from "react-day-picker";

interface MetaData {
  total: number;
  lastPage: number;
  currentPage: number;
  perPage: number;
  prev: number | null;
  next: number | null;
}

interface CollectionOrderResponse {
  data: CollectionOrder[];
  meta: MetaData;
}

interface ErrorResponse {
  success: false;
  error: {
    message: string;
    statusCode: number;
  };
}

export const useCollectionOrders = (
  page: number,
  perPage: number,
  searchTerm: string,
  selectedWilaya: string,
  status: string,
  selectedCollector: string,
  dateRange: DateRange | undefined
) => {
  const [collectionOrders, setCollectionOrders] = useState<CollectionOrder[]>(
    []
  );
  const [meta, setMeta] = useState<MetaData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const fetchCollectionOrders = async () => {
    setLoading(true);
    setError(null); // Reset error before fetching
    try {
      const response: CollectionOrderResponse | ErrorResponse =
        await getCollectionOrders({
          page,
          perPage,
          searchTerm,
          selectedWilaya,
          status,
          selectedCollector,
          dateRange,
        });

      if ("data" in response) {
        // Successful response
        setCollectionOrders(response.data);
        setMeta(response.meta);
      } else {
        console.log(response);
        // Error response
        setError(response);
      }
    } catch (err) {
      // Fallback error message if error structure is unexpected
      setError({
        success: false,
        error: { message: "Something went wrong", statusCode: 500 },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollectionOrders();
  }, [
    page,
    perPage,
    searchTerm,
    selectedWilaya,
    status,
    selectedCollector,
    dateRange,
  ]);

  const refresh = () => {
    fetchCollectionOrders();
  };

  return { collectionOrders, refresh, meta, loading, error };
};
